import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Tab,
  Tabs,
  IconButton,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
  Alert,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useGarage } from '../../contexts/GarageContext';
import { apiService } from '../../services/api';
import AppointmentsList from './AppointmentsList';
import FiltersModal from './FiltersModal';
import NewAppointmentModal from './NewAppointmentModal';
import DateRangeCalendar from './DateRangeCalendar';

const Appointments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedGarage } = useGarage();
  
  // Tab state - Upcoming and Custom Date as specified
  const [currentTab, setCurrentTab] = useState(0);
  const tabLabels = ['Upcoming', 'Custom Date'];
  
  // Filter state
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    customer_ids: [],
    product_ids: [],
    addon_ids: [],
    payment_status: '',
    appointment_status: ''
  });
  
  // Store names for filter chips display
  const [filterLabels, setFilterLabels] = useState({
    customers: {},
    products: {},
    addons: {}
  });
  
  // Store garage settings data to pass to FiltersModal
  const [garageSettings, setGarageSettings] = useState({
    products: [],
    addons: []
  });
  
  // New appointment modal
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);
  
  // Notification states
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Date range for custom date tab - default to today
  const [dateRange, setDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  // Fetch garage settings when component mounts
  useEffect(() => {
    const fetchGarageSettings = async () => {
      if (!selectedGarage?.id) return;
      
      try {
        const garageSettingsRes = await apiService.getGarageSettings(selectedGarage.id);
        const settings = garageSettingsRes.data || garageSettingsRes;
        
        const enabledProducts = (settings.selected_products || []).filter(product => product.is_enabled).map(product => ({
          ...product,
          id: product.product_id || product.id
        }));
        const enabledAddons = (settings.selected_addons || []).filter(addon => addon.is_enabled).map(addon => ({
          ...addon,
          id: addon.addon_id || addon.id
        }));
        
        setGarageSettings({
          products: enabledProducts,
          addons: enabledAddons
        });
      } catch (error) {
        console.error('Error fetching garage settings:', error);
        setGarageSettings({
          products: [],
          addons: []
        });
      }
    };
    
    fetchGarageSettings();
  }, [selectedGarage?.id]);

  // Format date range for input display
  const formatDateRangeDisplay = () => {
    if (!dateRange.startDate || !dateRange.endDate) return '';
    
    const formatDate = (date) => date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
    
    if (dateRange.startDate.toDateString() === dateRange.endDate.toDateString()) {
      return formatDate(dateRange.startDate);
    }
    
    return `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Handle filter apply
  const handleFiltersApply = (filters) => {
    setActiveFilters(filters);
    setFiltersOpen(false);
    
    // Merge the labels passed from FiltersModal with existing labels
    if (filters.labels) {
      setFilterLabels(prevLabels => ({
        customers: { ...prevLabels.customers, ...filters.labels.customers },
        products: { ...prevLabels.products, ...filters.labels.products },
        addons: { ...prevLabels.addons, ...filters.labels.addons }
      }));
    }
  };

  // Remove specific filter
  const removeFilter = (category, value) => {
    setActiveFilters(prev => {
      if (category === 'payment_status' || category === 'appointment_status') {
        // Single selection fields - set to empty string
        return {
          ...prev,
          [category]: ''
        };
      } else {
        // Array fields - remove the specific value
        return {
          ...prev,
          [category]: prev[category].filter(item => item !== value)
        };
      }
    });
  };

  // Remove all filters
  const removeAllFilters = () => {
    setActiveFilters({
      customer_ids: [],
      product_ids: [],
      addon_ids: [],
      payment_status: '',
      appointment_status: ''
    });
    setFilterLabels({
      customers: {},
      products: {},
      addons: {}
    });
  };

  // Ref to hold the refresh function from AppointmentsList
  const refreshAppointmentsRef = React.useRef(null);

  // Handle appointment creation success
  const handleAppointmentSuccess = () => {
    setNewAppointmentOpen(false);
    setSuccess('Appointment created successfully!');
    // Refresh appointments list
    if (refreshAppointmentsRef.current) {
      refreshAppointmentsRef.current();
    }
  };

  // Handle appointment creation error
  const handleAppointmentError = (errorMessage) => {
    setError(errorMessage || 'Failed to create appointment. Please try again.');
  };

  // Check if any filters are active
  const hasActiveFilters = 
    activeFilters.customer_ids.length > 0 ||
    activeFilters.product_ids.length > 0 ||
    activeFilters.addon_ids.length > 0 ||
    activeFilters.payment_status !== '' ||
    activeFilters.appointment_status !== '';

  // Get filter chips for display
  const getFilterChips = () => {
    const chips = [];
    
    // Payment Status chips (single selection)
    if (activeFilters.payment_status) {
      chips.push({
        label: activeFilters.payment_status,
        category: 'payment_status',
        value: activeFilters.payment_status
      });
    }
    
    // Appointment Status chips (single selection)
    if (activeFilters.appointment_status) {
      chips.push({
        label: activeFilters.appointment_status,
        category: 'appointment_status', 
        value: activeFilters.appointment_status
      });
    }
    
    // Customer chips - show customer name and vehicle reg no
    activeFilters.customer_ids.forEach(customerId => {
      const customerLabel = filterLabels.customers[customerId] || `Customer: ${customerId}`;
      chips.push({
        label: customerLabel,
        category: 'customer_ids',
        value: customerId
      });
    });
    
    // Product chips
    activeFilters.product_ids.forEach(productId => {
      const productLabel = filterLabels.products[productId] || `Product: ${productId}`;
      chips.push({
        label: productLabel,
        category: 'product_ids',
        value: productId
      });
    });
    
    // Addon chips
    activeFilters.addon_ids.forEach(addonId => {
      const addonLabel = filterLabels.addons[addonId] || `Addon: ${addonId}`;
      chips.push({
        label: addonLabel,
        category: 'addon_ids',
        value: addonId
      });
    });
    
    return chips;
  };

  return (
    <Box>
      {/* Header - New Appointment Button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        mb: 3
      }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setNewAppointmentOpen(true)}
          sx={{ 
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1
          }}
        >
          New Appointment
        </Button>
      </Box>

      {/* Tabs and Filters Row */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2
      }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ 
            flex: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: { xs: '0.875rem', md: '1rem' },
              minWidth: { xs: 80, md: 120 },
              py: 1.5,
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 600
              },
              '&:hover': {
                color: 'primary.main'
              }
            },
            '& .MuiTabs-indicator': {
              height: 3,
              borderRadius: 1.5,
              backgroundColor: 'primary.main'
            }
          }}
        >
          {tabLabels.map((label, index) => (
            <Tab key={label} label={label} />
          ))}
        </Tabs>
        
        <IconButton
          onClick={() => setFiltersOpen(true)}
          sx={{
            border: '1px solid',
            borderColor: hasActiveFilters 
              ? 'primary.main' 
              : theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
            borderRadius: { xs: 1.5, md: 2 },
            flexShrink: 0,
            ml: { xs: 1, md: 2 },
            p: { xs: 1, md: 1.25 },
            backgroundColor: hasActiveFilters 
              ? theme.palette.mode === 'dark' 
                ? 'rgba(144, 202, 249, 0.08)' 
                : 'rgba(25, 118, 210, 0.04)'
              : 'transparent',
            color: hasActiveFilters 
              ? 'primary.main' 
              : theme.palette.mode === 'dark' ? 'text.secondary' : 'grey.600',
            transition: theme.transitions.create(['border-color', 'background-color', 'color'], {
              duration: theme.transitions.duration.shorter
            }),
            '&:hover': {
              backgroundColor: hasActiveFilters
                ? theme.palette.mode === 'dark'
                  ? 'rgba(144, 202, 249, 0.12)'
                  : 'rgba(25, 118, 210, 0.08)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.04)'
                  : 'rgba(0, 0, 0, 0.04)',
              borderColor: 'primary.main',
              color: 'primary.main'
            }
          }}
        >
          <FilterListIcon sx={{ fontSize: { xs: '20px', md: '24px' } }} />
        </IconButton>
      </Box>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box sx={{ 
            mb: { xs: 2, md: 2.5 },
            p: { xs: 1.5, md: 2 },
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.02)' 
              : 'rgba(0, 0, 0, 0.015)',
            borderRadius: { xs: 2, md: 2.5 },
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200'
          }}>
            <Stack 
              direction="row" 
              spacing={{ xs: 1, md: 1.5 }}
              alignItems="center" 
              flexWrap="wrap" 
              gap={{ xs: 1, md: 1.5 }}
              sx={{ 
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                flexWrap: 'wrap',
                gap: { xs: 1, md: 1.5 }
              }}>
                <Typography 
                  variant="body2" 
                  sx={{
                    color: theme.palette.mode === 'dark' ? 'text.primary' : 'grey.700',
                    fontWeight: 500,
                    fontSize: { xs: '0.8rem', md: '0.875rem' },
                    flexShrink: 0
                  }}
                >
                  Active Filters:
                </Typography>
                {getFilterChips().map((chip, index) => (
                  <Chip
                    key={index}
                    label={chip.label}
                    size="small"
                    onDelete={() => removeFilter(chip.category, chip.value)}
                    deleteIcon={<CloseIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />}
                    sx={{
                      backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(144, 202, 249, 0.12)'
                        : 'rgba(25, 118, 210, 0.08)',
                      color: 'primary.main',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', md: '0.8rem' },
                      height: { xs: '28px', md: '32px' },
                      borderRadius: { xs: 1.5, md: 2 },
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark'
                        ? 'rgba(144, 202, 249, 0.2)'
                        : 'rgba(25, 118, 210, 0.15)',
                      transition: theme.transitions.create(['background-color', 'border-color'], {
                        duration: theme.transitions.duration.shorter
                      }),
                      '& .MuiChip-deleteIcon': {
                        color: 'primary.main',
                        transition: theme.transitions.create(['color', 'background-color'], {
                          duration: theme.transitions.duration.shorter
                        }),
                        '&:hover': {
                          color: 'primary.dark',
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.1)'
                            : 'rgba(255, 255, 255, 0.8)'
                        }
                      },
                      '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                          ? 'rgba(144, 202, 249, 0.16)'
                          : 'rgba(25, 118, 210, 0.12)',
                        borderColor: 'primary.main'
                      }
                    }}
                  />
                ))}
              </Box>
              
              <Button
                size="small"
                variant="text"
                onClick={removeAllFilters}
                sx={{ 
                  color: 'primary.main',
                  fontSize: { xs: '0.75rem', md: '0.8rem' },
                  fontWeight: 500,
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: { xs: 1, md: 1.5 },
                  py: 0.5,
                  borderRadius: { xs: 1, md: 1.5 },
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(144, 202, 249, 0.08)'
                      : 'rgba(25, 118, 210, 0.04)',
                    textDecoration: 'none'
                  }
                }}
              >
                Clear All
              </Button>
            </Stack>
          </Box>
      )}

      {/* Custom Date Range (only show when Custom Date tab is active) */}
      {currentTab === 1 && (
        <Box sx={{ mb: 3 }}>
          {/* Date Range Input */}
          <TextField
            value={formatDateRangeDisplay()}
            placeholder="Select date range"
            onClick={() => setDateRangeOpen(true)}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              )
            }}
            sx={{
              minWidth: { xs: '100%', sm: '320px' },
              maxWidth: { xs: '100%', md: '450px' },
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': {
                  borderColor: 'primary.main'
                }
              }
            }}
          />
        </Box>
      )}

      {/* Date Range Modal */}
      <Dialog
        open={dateRangeOpen}
        onClose={() => setDateRangeOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, md: 3 },
            m: { xs: 0, md: 2 },
            height: '100%',
            maxHeight: { xs: '100vh', md: '600px' },
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0
        }}>
          <Typography variant="body1" fontWeight="600" sx={{ fontSize: '1.25rem' }}>
            Select Date Range
          </Typography>
          <IconButton 
            onClick={() => setDateRangeOpen(false)} 
            size="small"
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                backgroundColor: 'action.hover' 
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DateRangeCalendar
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={(range) => {
            setDateRange(range);
            setDateRangeOpen(false);
          }}
          onClose={() => setDateRangeOpen(false)}
        />
      </Dialog>

      {/* Appointments List */}
      <AppointmentsList
        filters={activeFilters}
        dateFilter={currentTab === 0 ? 'upcoming' : 'custom'}
        dateRange={currentTab === 1 ? dateRange : null}
        onNewAppointment={() => setNewAppointmentOpen(true)}
        onRefresh={(refreshFn) => {
          refreshAppointmentsRef.current = refreshFn;
        }}
        availableProducts={garageSettings.products}
        availableAddons={garageSettings.addons}
      />

      {/* Modals */}
      <FiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        onApply={handleFiltersApply}
        currentFilters={activeFilters}
        availableProducts={garageSettings.products}
        availableAddons={garageSettings.addons}
      />

      <NewAppointmentModal
        open={newAppointmentOpen}
        onClose={() => setNewAppointmentOpen(false)}
        garageId={selectedGarage?.id}
        onSuccess={handleAppointmentSuccess}
        onError={handleAppointmentError}
        availableProducts={garageSettings.products}
        availableAddons={garageSettings.addons}
        key={newAppointmentOpen ? 'open' : 'closed'} // Force re-mount to reset data
      />

      {/* Success Notification */}
      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity="success" 
            onClose={() => setSuccess(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {success}
          </Alert>
        </Snackbar>
      )}

      {/* Error Notification */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {error}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default Appointments;